/**
 * useMicAliases — iter-222
 *
 * Tiny hook that holds per-input mic aliases. Lets the user rename
 * `Mic 7` to `SM7B` (or whatever they want), persisted to
 * localStorage so the labels survive reloads + new sessions.
 *
 * Storage shape (one global map; keyed by 0-based input index):
 *   { "0": "Lead Vocal", "6": "SM7B", ... }
 *
 * Why per-index (not per-deviceId): users typically keep the same
 * mic plugged into the same physical input slot across sessions; if
 * they swap interfaces they can re-label in 5 seconds. Per-device is
 * over-engineered for the current need.
 *
 * Exposed:
 *   aliases:   the alias map (always defined, never null)
 *   getLabel:  (i, fallback?) => alias for input i, or the fallback
 *              (defaults to `Mic ${i + 1}`)
 *   setAlias:  (i, alias|null) => persist + broadcast change
 *   clearAll:  () => wipe localStorage entry (returns aliases to {})
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "elpm:mic-aliases";

function readFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
    return {};
  } catch (e) { void e; return {}; }
}

function writeToStorage(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) { void e; }
}

export function useMicAliases() {
  const [aliases, setAliases] = useState(readFromStorage);

  // Cross-tab sync — if the user opens two studio tabs, renaming Mic 7
  // in one should reflect in the other on the next focus.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setAliases(readFromStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setAlias = useCallback((i, alias) => {
    setAliases((prev) => {
      const next = { ...prev };
      const trimmed = typeof alias === "string" ? alias.trim() : "";
      if (!trimmed) delete next[String(i)];
      else next[String(i)] = trimmed.slice(0, 28); // hard cap so UI stays compact
      writeToStorage(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    writeToStorage({});
    setAliases({});
  }, []);

  const getLabel = useCallback((i, fallback) => {
    const a = aliases[String(i)];
    if (a && a.length > 0) return a;
    return fallback ?? `Mic ${i + 1}`;
  }, [aliases]);

  return { aliases, getLabel, setAlias, clearAll };
}

/** Stand-alone helper for non-hook contexts (e.g. recording-name builders).
 *  Reads the same localStorage map without subscribing. */
export function readMicAliasSync(i, fallback) {
  try {
    const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const a = map?.[String(i)];
    if (a && typeof a === "string" && a.length > 0) return a;
  } catch (e) { void e; }
  return fallback ?? `Mic ${i + 1}`;
}
